import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { ref, set, get, update } from 'firebase/database';
import { auth, database } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  // Register new user
  const register = async (email, password, name, phone) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with display name
      await updateProfile(user, { displayName: name });
      
      // Send email verification
      await sendEmailVerification(user);
      
      // Create user data in Realtime Database
      await set(ref(database, `users/${user.uid}`), {
        personalInfo: {
          name,
          email,
          phone: phone || '',
          role: 'user',
          registeredAt: Date.now(),
          emailVerified: false,
          provider: 'email'
        },
        membership: {
          status: 'inactive',
          startDate: null,
          expiryDate: null
        },
        wallet: {
          balance: 0,
          entries: {},
          transactions: {}
        }
      });
      
      return { success: true, user };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  // Google Sign In
  const googleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // After successful sign-in, check if user exists in database
      try {
        const userSnapshot = await get(ref(database, `users/${user.uid}`));
        
        if (!userSnapshot.exists()) {
          // Create new user data in Realtime Database for Google sign-in
          await set(ref(database, `users/${user.uid}`), {
            personalInfo: {
              name: user.displayName || 'Google User',
              email: user.email,
              phone: user.phoneNumber || '',
              role: 'user',
              registeredAt: Date.now(),
              emailVerified: user.emailVerified,
              provider: 'google',
              lastLogin: Date.now()
            },
            membership: {
              status: 'inactive',
              startDate: null,
              expiryDate: null
            },
            wallet: {
              balance: 0,
              entries: {},
              transactions: {}
            }
          });
        } else {
          // Update existing user's last login time
          await update(ref(database, `users/${user.uid}/personalInfo`), {
            lastLogin: Date.now()
          });
        }
      } catch (dbError) {
        console.error('Database operation error:', dbError);
        // Don't throw here - user is still authenticated
        // Just log the error and continue
      }
      
      return { success: true, user };
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      let errorMessage = 'Failed to sign in with Google';
      
      // Handle specific error codes
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in popup was closed before completing';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Sign-in was cancelled';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage = 'This domain is not authorized for Google sign-in. Please contact support.';
          break;
        default:
          errorMessage = error.message || 'Google sign-in failed. Please try again.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if email is verified (only for email/password accounts)
      if (!user.emailVerified) {
        await signOut(auth);
        return { 
          success: false, 
          error: 'Please verify your email before logging in. Check your inbox for the verification link.',
          needsVerification: true 
        };
      }
      
      // Update last login
      try {
        await update(ref(database, `users/${user.uid}/personalInfo`), {
          lastLogin: Date.now()
        });
      } catch (dbError) {
        console.error('Failed to update last login:', dbError);
      }
      
      return { success: true, user };
    } catch (error) {
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      }
      return { success: false, error: errorMessage };
    }
  };

  // Resend verification email
  const resendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await sendEmailVerification(auth.currentUser);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    }
    return { success: false, error: 'No user logged in or email already verified' };
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      }
      return { success: false, error: errorMessage };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Fetch user data from database
  const fetchUserData = async (uid) => {
    try {
      const snapshot = await get(ref(database, `users/${uid}`));
      if (snapshot.exists()) {
        setUserData(snapshot.val());
        return snapshot.val();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Fetch user data regardless of verification status
        await fetchUserData(user.uid);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    register,
    login,
    logout,
    googleSignIn,
    resetPassword,
    resendVerificationEmail,
    fetchUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};